import React, { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import SearchSelect from "components/ui/SearchSelect";
import Button from "components/ui/Button";
import { PRODUCT_OPTIONS } from "components/financial/ProductInput";

// A saved tag as editable rows: its split, or its one product at 100%.
const rowsFromTag = (tag) => {
  if (tag?.splits?.length) {
    return tag.splits.map((s) => ({ product: s.product, percent: String(s.percent) }));
  }
  if (tag?.product) return [{ product: tag.product, percent: "100" }];
  return [{ product: "", percent: "100" }];
};

// "90% Subscription · 10% Sandy Campaign" — for showing a saved split.
export const describeSplit = (tag) =>
  tag?.splits?.length
    ? tag.splits.map((s) => `${s.percent}% ${s.product}`).join(" · ")
    : tag?.product ?? "";

/**
 * Edits how one expense category's cost is shared across products.
 *
 * Props:
 *   tag    — the category's saved product tag, or undefined
 *   onSave — ([{ product, percent }]) => void
 *   isSaving
 */
const ProductSplitEditor = ({ tag, onSave, isSaving = false }) => {
  const [rows, setRows] = useState(() => rowsFromTag(tag));

  const total = rows.reduce((sum, r) => sum + (parseFloat(r.percent) || 0), 0);
  const roundedTotal = Math.round(total * 100) / 100;
  const chosen = rows.map((r) => r.product).filter(Boolean);
  const hasDuplicate = new Set(chosen).size !== chosen.length;
  const isValid =
    rows.every((r) => r.product && parseFloat(r.percent) > 0) &&
    !hasDuplicate &&
    Math.abs(total - 100) <= 0.01;

  const unusedOptions = useMemo(
    () => PRODUCT_OPTIONS.filter((o) => !chosen.includes(o.value)),
    [chosen],
  );

  const updateRow = (index, patch) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { product: "", percent: String(Math.max(0, Math.round((100 - total) * 100) / 100)) },
    ]);

  return (
    <div
      className="flex flex-col gap-2 px-4 py-3 bg-lightergray border-t border-mediumgray"
      onClick={(e) => e.stopPropagation()}
    >
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <SearchSelect
              value={PRODUCT_OPTIONS.find((o) => o.value === row.product) ?? null}
              onChange={(option) => updateRow(index, { product: option?.value ?? "" })}
              options={[
                ...PRODUCT_OPTIONS.filter((o) => o.value === row.product),
                ...unusedOptions,
              ]}
              placeholder="— Select product —"
            />
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={row.percent}
              onChange={(e) => updateRow(index, { percent: e.target.value })}
              className="form-input w-20 border border-mediumgray text-right"
            />
            <span className="text-sm text-darkgray">%</span>
          </div>
          <button
            type="button"
            disabled={rows.length === 1}
            onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
            className="disabled:opacity-30"
          >
            <Trash2 className="delete-icon" />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={addRow}
          disabled={unusedOptions.length === 0}
          className="flex items-center gap-1 text-xs text-darkgray hover:text-darkergray disabled:opacity-40"
        >
          <Plus className="w-3.5 h-3.5" /> Add product
        </button>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs tabular-nums ${isValid ? "text-darkgray" : "text-red-500"}`}
          >
            {hasDuplicate ? "Each product once" : `Total ${roundedTotal}% of 100%`}
          </span>
          <Button
            onClick={() =>
              onSave(rows.map((r) => ({ product: r.product, percent: parseFloat(r.percent) })))
            }
            disabled={!isValid || isSaving}
            loading={isSaving}
            className="black-button disabled:opacity-40"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductSplitEditor;
